<template>
  <div class="navigation-shell">
    <div
      v-if="isMobile && mobileMenuOpen"
      class="nav-mobile-backdrop"
      @click="closeMobileMenu"
      aria-hidden="true"
    ></div>

    <header v-if="isMobile" class="mobile-top-bar">
      <button
        type="button"
        class="mobile-menu-btn"
        @click="toggleMobileMenu"
        :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
        aria-label="Toggle navigation menu"
      >
        <i class="bx" :class="mobileMenuOpen ? 'bx-x' : 'bx-menu'"></i>
      </button>
      <div class="mobile-top-bar__brand">
        <span class="mobile-top-bar__title">StaffSync</span>
        <span class="mobile-top-bar__subtitle">Smart HR</span>
      </div>
    </header>

  <!-- Modern Navigation Component -->
  <nav class="modern-nav" :class="{ 'collapsed': isCollapsed, 'mobile-open': mobileMenuOpen }">
    <!-- Logo and Brand Section -->
    <div class="nav-brand">
      <div class="brand-content" @click="toggleNav">
        <!--<div class="brand-logo">
          <img :src="county_logo" alt="County Logo" class="logo-img">
        </div>-->
        <div class="brand-text" v-if="!isCollapsed">
          <h3 class="brand-title">STAFFSYNC</h3>
          <p class="brand-subtitle">Smart HR System</p>
        </div>
        <button class="nav-toggle-btn" @click.stop="toggleNav">
          <i class="bx toggle-icon" :class="isCollapsed ? 'bx-chevron-right' : 'bx-chevron-left'"></i>
        </button>
      </div>
    </div>

    <div class="nav-profile" v-if="!isCollapsed" @click.stop>
      <div class="profile-avatar">
        <div class="avatar-circle">
          <i class="bx bx-user"></i>
        </div>
      </div>

      <div class="profile-info">
        <h4 class="profile-name">{{ sessionNames }}</h4>
      </div>

      <div class="profile-actions">
        <button class="profile-action-btn" @click.stop="toggleProfileMenu">
          <i class="bx bx-chevron-down"></i>
        </button>
      </div>

      <!-- DROPDOWN MUST LIVE HERE -->
      <transition name="fade">
        <div
          class="profile-dropdown"
          v-if="showProfileMenu"
          @click.stop
        >
          <div class="dropdown-content">
            <a class="dropdown-item" @click="goTo('employee-profile')">
              <i class="bx bx-user-circle"></i>
              <span>My Profile</span>
            </a>

            <a class="dropdown-item" @click="goTo('change-password')">
              <i class="bx bx-lock-alt"></i>
              <span>Change Password</span>
            </a>

            <div class="dropdown-divider"></div>

            <a class="dropdown-item logout" @click="logout('/')">
              <i class="bx bx-log-out"></i>
              <span>Logout</span>
            </a>
          </div>
        </div>
      </transition>
    </div>


    <!-- Navigation Menu (ref for scroll-to-active) -->
    <div ref="navMenu" class="nav-menu">
      <!-- Search Bar -->
      <div class="nav-search" v-if="!isCollapsed">
        <div class="search-wrapper">
          <i class="bx bx-search search-icon"></i>
          <input 
            type="text" 
            class="search-input" 
            placeholder="Search..."
            v-model="searchQuery"
            @input="filterMenuItems"
          >
        </div>
      </div>

      <!-- Menu Items -->
      <div class="menu-section" v-for="(section, index) in filteredMenuSections" :key="index">
        <div class="section-header" v-if="section.title && !isCollapsed">
          <h4 class="section-title">{{ section.title }}</h4>
        </div>
        
        <div class="menu-items">
          <div 
            v-for="item in section.items" 
            :key="item.id"
            class="menu-item-wrapper"
          >
            <!-- Menu Item with Children (Dropdown) -->
            <template v-if="item.children && item.children.length > 0">
              <div 
                class="menu-item-header"
                :class="{ 
                  'active': isActive(item.route) || isActiveParent(item),
                  'collapsed': isCollapsed
                }"
                @click.stop="handleParentWithChildrenClick(item, $event)"
              >
                <div class="menu-icon">
                  <i :class="item.icon"></i>
                </div>
                <div class="menu-text" v-if="!isCollapsed">
                  <span class="menu-title">{{ item.title }}</span>
                  <span class="menu-badge" v-if="item.badge">{{ item.badge }}</span>
                </div>
                <div class="menu-arrow" v-if="!isCollapsed">
                  <i class="bx" :class="expandedItems[item.id] ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
                </div>
                <div class="menu-arrow-collapsed" v-if="isCollapsed && item.children?.length">
                  <i class="bx bx-chevron-right"></i>
                </div>
                <div class="menu-tooltip" v-if="isCollapsed">
                  {{ item.title }}
                </div>
              </div>

              <!-- Dropdown Submenu - Below the header -->
              <transition name="slide-down">
                <div 
                  class="submenu"
                  v-show="expandedItems[item.id] && !isCollapsed"
                >
                  <div 
                    v-for="child in visibleChildren(item)" 
                    :key="child.id"
                    class="submenu-item"
                    :class="{ 'active': isActive(child.route) }"
                    @click="handleMenuItemClick(child, $event)"
                  >
                    <div class="submenu-icon">
                      <i :class="child.icon"></i>
                    </div>
                    <div class="submenu-text">
                      <span class="submenu-title">{{ child.title }}</span>
                      <span class="submenu-badge" v-if="child.badge">{{ child.badge }}</span>
                    </div>
                  </div>
                </div>
              </transition>
            </template>

            <!-- Single Menu Item (no children) -->
            <div 
              v-else
              class="menu-item"
              :class="{ 
                'active': isActive(item.route),
                'collapsed': isCollapsed
              }"
              @click="handleMenuItemClick(item, $event)"
            >
              <div class="menu-icon">
                <i :class="item.icon"></i>
              </div>
              <div class="menu-text" v-if="!isCollapsed">
                <span class="menu-title">{{ item.title }}</span>
                <span class="menu-badge" v-if="item.badge">{{ item.badge }}</span>
              </div>
              <div class="menu-tooltip" v-if="isCollapsed">
                {{ item.title }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Footer -->
    <div class="nav-footer" v-if="!isCollapsed">
      <div class="footer-content">
        <div class="footer-item" @click="toggleTheme">
          <i class="bx bx-sun"></i>
          <span>Theme</span>
        </div>
      </div>
    </div>
  </nav>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, county_logo, execute, getRight, refreshSessionRoles } from "@/api";

export default {
  name: 'Navigation',
  
  data() {
    return {
      isCollapsed: false,
      isMobile: false,
      mobileMenuOpen: false,
      showProfileMenu: false,
      searchQuery: '',
      expandedItems: {},
      filteredMenuSections: [],
      userRole: '',
      mutationObserver: null,
      navScrollKey: 'staffsync.nav.scrollTop',
      
      // Restructured menu with logical groupings
      allMenuSections: [
        {
          title: 'Super Admin',
          items: [
            {
              id: 'super-admin',
              title: 'Companies',
              icon: 'mdi mdi-domain',
              route: 'super-admin',
              permission: 'SUPER ADMIN'
            }
          ]
        },
        {
          title: 'Dashboard',
          items: [
            {
              id: 'dashboard',
              title: 'Dashboard',
              icon: 'bx bx-home-circle',
              route: 'dashboard',
              permission: 'DASHBOARD'
            }
          ]
        },
        {
          title: 'Staff Establishment',
          items: [
            {
              id: 'employee-register',
              title: 'Employee Register',
              icon: 'mdi mdi-account-group',
              route: 'employee-register',
              permission: 'EMPLOYEE REGISTER'
            },
            {
              id: 'employee-archive',
              title: 'Employee Archive',
              icon: 'mdi mdi-archive',
              route: 'employee-archive',
              permission: 'EMPLOYEE REGISTER'
            },
            {
              id: 'employee-approvals',
              title: 'Employee Approvals',
              icon: 'mdi mdi-check-decagram',
              route: null,
              permission: 'EMPLOYEE APPROVALS',
              children: [
                {
                  id: 'employee-approvals-new',
                  title: 'Pending Approvals - New Employees',
                  icon: 'mdi mdi-account-check',
                  route: 'employee-approvals-new',
                  permission: 'PENDING APPROVALS NEW EMPLOYEES'
                },
                {
                  id: 'employee-approvals-edit-approvals',
                  title: 'Pending Approvals - Data Edits',
                  icon: 'mdi mdi-pencil',
                  route: 'employee-approvals-edit-approvals',
                  permission: 'PENDING APPROVALS DATA EDITS'
                },
                {
                  id: 'employee-approvals-approved',
                  title: 'Approved Recruits',
                  icon: 'mdi mdi-account-check-outline',
                  route: 'employee-approvals-approved',
                  permission: 'EMPLOYEE APPROVALS'
                },
                {
                  id: 'employee-approvals-rejected',
                  title: 'Rejected',
                  icon: 'mdi mdi-close-circle',
                  route: 'employee-approvals-rejected',
                  permission: 'REJECTED APPROVALS'
                }
              ]
            },
            {
              id: 'department-management',
              title: 'Departments',
              icon: 'mdi mdi-account-multiple',
              route: 'department-management',
              permission: 'DEPARTMENT MANAGEMENT'
            },
            {
              id: 'employee-lifecycle',
              title: 'Employee Lifecycle',
              icon: 'mdi mdi-timeline-clock-outline',
              route: 'employee-lifecycle',
              permission: ['RECRUITMENT', 'PROMOTIONS', 'TRANSFERS', 'TERMINATION', 'EMPLOYEE REGISTER']
            },
            {
              id: 'hr-transfers',
              title: 'Transfers',
              icon: 'mdi mdi-transfer',
              route: 'hr-transfers',
              permission: 'TRANSFERS'
            },
            {
              id: 'hr-promotions',
              title: 'Promotions',
              icon: 'mdi mdi-arrow-up-bold',
              route: 'hr-promotions',
              permission: 'PROMOTIONS'
            },
            {
              id: 'disciplinary-management',
              title: 'Disciplinary',
              icon: 'mdi mdi-gavel',
              route: 'disciplinary-management',
              permission: 'DISCIPLINARY MANAGEMENT'
            },
            {
              id: 'hr-case-management',
              title: 'HR Cases',
              icon: 'mdi mdi-briefcase-check',
              route: 'hr-case-management',
              permission: 'HR CASE MANAGEMENT'
            },
            {
              id: 'psychosocial-management',
              title: 'Psychosocial',
              icon: 'mdi mdi-heart-pulse',
              route: 'psychosocial-management',
              permission: 'PSYCHOSOCIAL MANAGEMENT'
            },
            {
              id: 'separation',
              title: 'Separation',
              icon: 'mdi mdi-logout-variant',
              route: null,
              permission: 'SEPARATION',
              children: [
                {
                  id: 'separation-termination',
                  title: 'Termination',
                  icon: 'mdi mdi-close-circle',
                  route: 'separation-termination',
                  permission: 'TERMINATION'
                },
                {
                  id: 'separation-fnf',
                  title: 'FNF',
                  icon: 'mdi mdi-cash',
                  route: 'separation-fnf',
                  permission: 'FNF'
                }
              ]
            },
            {
              id: 'recruitment',
              title: 'Recruitment',
              icon: 'mdi mdi-account-search',
              route: 'recruitment',
              permission: [
                'RECRUITMENT',
                'VACANCY MANAGEMENT',
                'SHORTLISTING MANAGEMENT',
                'INTERVIEW MANAGEMENT',
                'APPOINTMENT MANAGEMENT'
              ]
            }
          ]
        },
        {
          title: 'Attendance Management',
          items: [
            {
              id: 'shift-management',
              title: 'Shift Management',
              icon: 'mdi mdi-clock-outline',
              route: 'shift-management',
              permission: 'TIMESHEET' // or another appropriate permission if needed
            },
            {
              id: 'attendance-timesheet',
              title: 'Timesheet',
              icon: 'mdi mdi-clock-time-four',
              route: 'attendance-timesheet',
              permission: 'TIMESHEET'
            },
            {
              id: 'report-monthly',
              title: 'Manage Attendance',
              icon: 'mdi mdi-account-check',
              route: 'report-monthly',
              permission: 'MANAGE ATTENDANCE'
            },
            {
              id: 'remote-attendance-approvals',
              title: 'Remote Attendance Approvals',
              icon: 'mdi mdi-home-export-outline',
              route: 'remote-attendance-approvals',
              permission: 'REMOTE ATTENDANCE APPROVALS'
            },
            {
              id: 'work-locations',
              title: 'Clock-In Locations',
              icon: 'mdi mdi-map-marker-radius',
              route: 'work-locations',
              permission: ['MANAGE ATTENDANCE', 'MANAGE WORK LOCATIONS']
            }
          ]
        },
        {
          title: 'Leave Management',
          items: [
            {
              id: 'attendance-leave',
              title: 'Leave Approval',
              icon: 'mdi mdi-calendar',
              route: 'attendance-leave',
              permission: 'MANAGE LEAVE'
            },
            {
              id: 'attendance-leave-manage',
              title: 'Leave Balances',
              icon: 'mdi mdi-calendar-remove',
              route: 'attendance-leave-manage',
              permission: 'MANAGE LEAVE'
            },
            {
              id: 'leave-types',
              title: 'Leave Types',
              icon: 'mdi mdi-calendar-remove',
              route: 'leave-types',
              permission: 'MANAGE LEAVE'
            }
          ]
        },
        {
          title: 'Performance & Appraisals',
          items: [
            {
              id: 'manager-dashboard',
              title: 'Manage Team Appraisals',
              icon: 'mdi mdi-account-supervisor',
              route: 'manager-dashboard',
              permission: 'MANAGER DASHBOARD'
            },
            {
              id: 'hr-dashboard',
              title: 'HR Dashboard',
              icon: 'mdi mdi-shield-account',
              route: 'hr-dashboard',
              permission: 'HR DASHBOARD'
            },
            {
              id: 'performance-reports',
              title: 'Reports & Analytics',
              icon: 'mdi mdi-chart-bar',
              route: 'performance-reports',
              permission: 'PERFORMANCE REPORTS'
            },
            {
              id: 'appraisal-cycles',
              title: 'Appraisal Cycles',
              icon: 'mdi mdi-calendar-check',
              route: 'appraisal-cycles',
              permission: 'APPRAISAL CYCLES'
            },
            {
              id: 'appraisal-form-management',
              title: 'Appraisal PDF Forms',
              icon: 'mdi mdi-file-pdf-box',
              route: 'appraisal-form-management',
              permission: 'APPRAISAL CYCLES'
            },
            {
              id: 'kpi-templates',
              title: 'KPI Templates',
              icon: 'mdi mdi-chart-box',
              route: 'kpi-templates',
              permission: 'KPI MANAGEMENT'
            },
            {
              id: 'pip-management',
              title: 'PIP Management',
              icon: 'mdi mdi-clipboard-check',
              route: 'pip-management',
              permission: 'PIP MANAGEMENT'
            },
            {
              id: 'hr-warnings',
              title: 'PIP',
              icon: 'mdi mdi-clipboard-check',
              route: 'hr-warnings',
              permission: 'WARNINGS'
            }
          ]
        },
        {
          title: 'Asset Management',
          items: [
            {
              id: 'assets',
              title: 'Assets',
              icon: 'mdi mdi-archive',
              route: 'assets',
              permission: 'ASSETS'
            }
          ]
        },
        {
          title: 'Reports & Analytics',
          items: [
            {
              id: 'report-daily',
              title: 'Attendance Reports',
              icon: 'mdi mdi-calendar-today',
              route: 'report-daily',
              permission: 'DAILY ATTENDANCE'
            },
            {
              id: 'off-duty-details',
              title: 'Unapproved Leave (15+ Days)',
              icon: 'mdi mdi-account-alert',
              route: 'off-duty-details',
              permission: 'OFF DUTY DETAILS'
            }
          ]
        },
        {
          title: 'Finance',
          items: [
            {
              id: 'payroll-assign-salaries',
              title: 'Assign Employee Salaries',
              icon: 'mdi mdi-cash-plus',
              route: 'payroll-assign-salaries',
              permission: ['PAYROLL SETUP & CONFIGURATION', 'PAYROLL SETUP', 'PAYROLL PERIODS', 'PAYROLL RECONCILIATION']
            },
            {
              id: 'payroll-setup',
              title: 'Payroll setup',
              icon: 'mdi mdi-cog-outline',
              route: 'payroll-setup',
              permission: ['PAYROLL SETUP & CONFIGURATION', 'PAYROLL SETUP', 'PAYROLL PERIODS', 'PAYROLL RECONCILIATION']
            },
            {
              id: 'payroll-hub',
              title: 'Payroll hub',
              icon: 'mdi mdi-view-dashboard-outline',
              route: 'payroll-hub',
              permission: ['PAYROLL RUN MANAGEMENT', 'PAYROLL PERIODS', 'PAYROLL RECONCILIATION']
            },
            {
              id: 'payroll-history',
              title: 'Payroll History',
              icon: 'mdi mdi-history',
              route: 'payroll-history',
              permission: ['PAYROLL RUN MANAGEMENT', 'PAYROLL PERIODS', 'PAYROLL RECONCILIATION', 'PAYROLL PAYMENT']
            },
            {
              id: 'recon-upload-payroll',
              title: 'Upload Payroll',
              icon: 'mdi mdi-upload',
              route: 'recon-upload-payroll',
              permission: 'UPLOAD PAYROLL'
            },
            {
              id: 'recon-history',
              title: 'Recon History',
              icon: 'mdi mdi-history',
              route: 'recon-history',
              permission: 'RECON HISTORY'
            },
            {
              id: 'imprest',
              title: 'Imprest',
              icon: 'mdi mdi-cash',
              route: null,
              permission: 'IMPREST',
              children: [
                {
                  id: 'imprest-request',
                  title: 'Imprest Request',
                  icon: 'mdi mdi-plus-circle',
                  route: 'imprest-request',
                  permission: 'IMPREST REQUEST'
                },
                {
                  id: 'imprest-approval',
                  title: 'Imprest Approval',
                  icon: 'mdi mdi-check-circle',
                  route: 'imprest-approval',
                  permission: 'IMPREST APPROVAL'
                },
                {
                  id: 'imprest-reconciliation',
                  title: 'Imprest Recon',
                  icon: 'mdi mdi-file-compare',
                  route: 'imprest-reconciliation',
                  permission: 'IMPREST RECONCILIATION'
                },
                {
                  id: 'imprest-history',
                  title: 'Imprest History',
                  icon: 'mdi mdi-history',
                  route: 'imprest-history',
                  permission: 'IMPREST HISTORY'
                }
              ]
            }
          ]
        },
        {
          title: 'Document Management',
          items: [
            {
              id: 'documents-general',
              title: 'General Documents',
              icon: 'mdi mdi-file',
              route: 'documents-general',
              permission: 'GENERAL DOCUMENTS'
            }
          ]
        },
        {
          title: 'Communications',
          items: [
            {
              id: 'hr-events',
              title: 'Events',
              icon: 'mdi mdi-calendar',
              route: 'hr-events',
              permission: 'EVENTS'
            },
            {
              id: 'hr-my-events',
              title: 'My Events',
              icon: 'mdi mdi-calendar',
              route: 'hr-my-events',
              permission: 'MY EVENTS'
            },
            {
              id: 'hr-meetings',
              title: 'Meetings',
              icon: 'mdi mdi-account-voice',
              route: 'hr-meetings',
              permission: 'MEETINGS'
            },
            {
              id: 'announcements',
              title: 'Announcements',
              icon: 'mdi mdi-bullhorn',
              route: 'announcements',
              permission: 'ANNOUNCEMENTS'
            },
            {
              id: 'polls-management',
              title: 'Manage Polls',
              icon: 'mdi mdi-poll',
              route: 'polls-management',
              permission: 'MANAGE POLLS'
            },
            {
              id: 'holidays-section',
              title: 'Holidays',
              icon: 'mdi mdi-beach',
              route: '/holidays',
              permission: 'HOLIDAYS',
              children: [
                {
                  id: 'holidays',
                  title: 'Manage Holidays',
                  icon: 'mdi mdi-calendar-check',
                  route: '/holidays',
                  permission: 'MANAGE HOLIDAYS'
                },
                {
                  id: 'holiday-categories',
                  title: 'Categories',
                  icon: 'mdi mdi-tag-multiple',
                  route: '/holiday-categories',
                  permission: 'HOLIDAY CATEGORIES'
                },
                {
                  id: 'holiday-calendar',
                  title: 'Calendar',
                  icon: 'mdi mdi-calendar-month',
                  route: '/holiday-calendar',
                  permission: 'HOLIDAY CALENDAR'
                },
                {
                  id: 'holiday-statistics',
                  title: 'Statistics',
                  icon: 'mdi mdi-chart-bar',
                  route: '/holiday-statistics',
                  permission: 'HOLIDAY STATISTICS'
                }
              ]
            }
          ]
        },
        {
          title: 'System Administration',
          items: [
            {
              id: 'org-structure',
              title: 'Organization Structure',
              icon: 'mdi mdi-family-tree',
              route: 'org-structure',
              permission: 'ORG STRUCTURE'
            },
            {
              id: 'position-role-management',
              title: 'Position & Roles',
              icon: 'mdi mdi-briefcase-account',
              route: 'position-role-management',
              permission: ['POSITION & ROLES', 'POSITION MANAGEMENT', 'Admin']
            },
            {
              id: 'position-structure',
              title: 'Position Structure',
              icon: 'mdi mdi-sitemap',
              route: 'position-structure',
              permission: ['POSITION & ROLES', 'POSITION MANAGEMENT', 'Admin']
            },
            {
              id: 'audit-logs',
              title: 'Audit Logs',
              icon: 'mdi mdi-clipboard-list-outline',
              route: 'audit-logs',
              permission: ['Admin', 'AUDIT LOGS']
            },
            {
              id: 'user-management',
              title: 'User Management',
              icon: 'mdi mdi-account-cog-outline',
              route: 'user-management',
              permission: ['Admin', 'USER MANAGEMENT']
            },
            {
              id: 'company-configuration',
              title: 'Company Configuration',
              icon: 'mdi mdi-domain',
              route: 'company-configuration',
              permission: ['Admin', 'COMPANY CONFIGURATION']
            }
          ]
        },
        {
          title: 'Employee Self-Service',
          items: [
            {
              id: 'employee-dashboard',
              title: 'My Dashboard',
              icon: 'mdi mdi-view-dashboard',
              route: 'employee-dashboard',
              permission: 'MY DASHBOARD'
            },
            {
              id: 'employee-profile',
              title: 'My Profile',
              icon: 'mdi mdi-account',
              route: 'employee-profile',
              permission: 'MY PROFILE'
            },
            {
              id: 'employee-leave',
              title: 'My Leave',
              icon: 'mdi mdi-calendar-month',
              route: null,
              permission: 'MY LEAVE',
              children: [
                {
                  id: 'employee-leave-apply',
                  title: 'Apply for Leave',
                  icon: 'mdi mdi-plus-circle',
                  route: 'employee-leave-apply',
                  permission: 'APPLY FOR LEAVE'
                },
                {
                  id: 'employee-leave-history',
                  title: 'Leave History',
                  icon: 'mdi mdi-history',
                  route: 'employee-leave-history',
                  permission: 'LEAVE HISTORY'
                },
                {
                  id: 'employee-leave-balance',
                  title: 'Leave Balance',
                  icon: 'mdi mdi-chart-donut',
                  route: 'employee-leave-balance',
                  permission: 'LEAVE BALANCE'
                },
                {
                  id: 'employee-leave-calendar',
                  title: 'Leave Calendar',
                  icon: 'mdi mdi-calendar',
                  route: 'employee-leave-calendar',
                  permission: 'LEAVE CALENDAR'
                }
              ]
            },
            {
              id: 'employee-attendance',
              title: 'My Attendance',
              icon: 'mdi mdi-clock-check-outline',
              route: null,
              permission: 'MY ATTENDANCE',
              children: [
                {
                  id: 'employee-attendance-records',
                  title: 'Attendance Records',
                  icon: 'mdi mdi-clipboard-text',
                  route: 'employee-attendance-records',
                  permission: 'ATTENDANCE RECORDS'
                },
                {
                  id: 'employee-attendance-clock',
                  title: 'Clock In/Out',
                  icon: 'mdi mdi-clock-in',
                  route: 'employee-attendance-clock',
                  permission: 'CLOCK IN OUT'
                },
                {
                  id: 'employee-attendance-summary',
                  title: 'Attendance Summary',
                  icon: 'mdi mdi-chart-bar',
                  route: 'employee-attendance-summary',
                  permission: 'ATTENDANCE SUMMARY'
                }
              ]
            },
            {
              id: 'employee-payroll',
              title: 'My Payroll',
              icon: 'mdi mdi-cash-multiple',
              route: null,
              permission: 'MY PAYROLL',
              children: [
                {
                  id: 'employee-payroll-payslips',
                  title: 'View Payslips',
                  icon: 'mdi mdi-file-document',
                  route: 'employee-payroll-payslips',
                  permission: 'VIEW PAYSLIPS'
                },
                {
                  id: 'employee-payroll-tax',
                  title: 'Tax Documents',
                  icon: 'mdi mdi-file-certificate',
                  route: 'employee-payroll-tax',
                  permission: 'TAX DOCUMENTS'
                },
                {
                  id: 'employee-payroll-history',
                  title: 'Payroll History',
                  icon: 'mdi mdi-history',
                  route: 'employee-payroll-history',
                  permission: 'PAYROLL HISTORY'
                }
              ]
            },
            {
              id: 'my-appraisals-ess',
              title: 'My Appraisals',
              icon: 'mdi mdi-file-chart',
              route: 'my-appraisals',
              permission: 'MY APPRAISALS'
            },
            {
              id: 'employee-self-assessment-ess',
              title: 'Self Assessment',
              icon: 'mdi mdi-clipboard-edit',
              route: 'employee-self-assessment',
              permission: ['SELF ASSESSMENT', 'EMPLOYEE SELF ASSESSMENT']
            },
            {
              id: 'employee-my-meetings',
              title: 'My Meetings',
              icon: 'mdi mdi-account-voice',
              route: 'employee-my-meetings',
              permission: 'MY MEETINGS'
            },
            {
              id: 'employee-my-warnings',
              title: 'My PIP',
              icon: 'mdi mdi-clipboard-check',
              route: 'employee-my-warnings',
              permission: 'MY WARNINGS'
            },
            {
              id: 'my-polls',
              title: 'Polls',
              icon: 'mdi mdi-poll',
              route: 'my-polls',
              permission: 'POLLS'
            },
            {
              id: 'employee-documents',
              title: 'My Documents',
              icon: 'mdi mdi-folder-account',
              route: null,
              permission: 'MY DOCUMENTS',
              children: [
                {
                  id: 'employee-documents-view',
                  title: 'View Documents',
                  icon: 'mdi mdi-file-eye',
                  route: 'employee-documents-view',
                  permission: 'VIEW DOCUMENTS'
                },
                {
                  id: 'employee-documents-upload',
                  title: 'Upload Documents',
                  icon: 'mdi mdi-file-upload',
                  route: 'employee-documents-upload',
                  permission: 'UPLOAD DOCUMENTS'
                }
              ]
            },
            {
              id: 'employee-announcements',
              title: 'Announcements',
              icon: 'mdi mdi-bullhorn',
              route: 'employee-announcements',
              permission: 'MY ANNOUNCEMENTS'
            }
          ]
        }
      ],
      
      sessionNames: '',
      county_logo: county_logo
    };
  },
  
  computed: {
    currentRoute() {
      return this.$route.name;
    }
  },

  async mounted() {
    await this.ensureSessionRoles();
    this.initializeMenu();
    this.syncExpandedToRoute();
    
    // Load saved theme, default to dark if none
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.body.setAttribute('data-theme', savedTheme);
    localStorage.setItem('theme', savedTheme);
    
    // Close menus when clicking outside
    document.addEventListener('click', this.handleClickOutside);
    
    this.handleResize = () => this.updateViewport();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('staffsync-roles-updated', this.onRolesUpdated);
    this.updateViewport();
    
    // Sync initial nav state with parent so content margin never overlaps
    this.$nextTick(() => {
      this.$emit('nav-toggle', this.isCollapsed);
      this.restoreNavScrollPosition();
      this.setupMutationObserver();
      this.scrollToActiveItem();
    });
    
    // Additional delay to ensure everything is rendered
    setTimeout(() => this.scrollToActiveItem(), 300);
  },
  
  beforeUnmount() {
    this.saveNavScrollPosition();
    document.body.classList.remove('staffsync-mobile-nav');
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('staffsync-roles-updated', this.onRolesUpdated);
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  },
  
  watch: {
    searchQuery() {
      this.filterMenuItems();
      this.$nextTick(() => this.scrollToActiveItem());
    },
    
    currentRoute: {
      handler() {
        this.closeMobileMenu();
        this.initializeMenu();
        this.syncExpandedToRoute();
        this.$nextTick(() => {
          this.scrollToActiveItem();
        });
        // Extra delay for DOM updates
        setTimeout(() => this.scrollToActiveItem(), 150);
      },
      immediate: true
    },

    expandedItems: {
      handler() {
        this.$nextTick(() => {
          this.scrollToActiveItem();
        });
      },
      deep: true
    }
  },

  methods: {
    saveNavScrollPosition() {
      const container = this.$refs.navMenu;
      if (container && typeof container.scrollTop === 'number') {
        sessionStorage.setItem(this.navScrollKey, String(container.scrollTop));
      }
    },
    restoreNavScrollPosition() {
      const container = this.$refs.navMenu;
      if (!container) return;
      const raw = sessionStorage.getItem(this.navScrollKey);
      const pos = Number(raw);
      if (Number.isFinite(pos) && pos >= 0) {
        container.scrollTop = pos;
      }
    },
    // Keep the parent menu expanded when the current route is a child, so the selected item stays visible
    syncExpandedToRoute() {
      const name = (this.$route.name || '').toString();
      const path = (this.$route.path || '').replace(/^#?\//, '');
      const routeMatch = (r) => {
        if (!r) return false;
        const s = String(r).replace(/^\/+/, '');
        return name === r || name === s || path === r || path === s || path.startsWith(s + '/');
      };

      const nextExpanded = {};
      this.allMenuSections.forEach(section => {
        (section.items || []).forEach(item => {
          if (item.children && item.children.length) {
            const isActiveParent = item.children.some(c => routeMatch(c.route));
            if (isActiveParent) nextExpanded[item.id] = true;
          }
        });
      });
      // Only expand the section that contains the current route so selection stays visible
      this.expandedItems = nextExpanded;
    },

    // Scroll the nav menu so the selected/active item is in view (not stuck at top)
    scrollToActiveItem() {
      const container = this.$refs.navMenu;
      if (!container) return;
      
      // Give the DOM a moment to update
      setTimeout(() => {
        const active = container.querySelector('.submenu-item.active, .menu-item.active, .menu-item-header.active');
        if (active) {
          this.scrollElementIntoView(container, active);
        }
      }, 50);
    },

    // Scroll container so that element is visible (used on click and for active item)
    scrollElementIntoView(container, element) {
      if (!container || !element || !container.contains(element)) return;
      
      const containerRect = container.getBoundingClientRect();
      const elRect = element.getBoundingClientRect();
      
      // Check if element is already visible
      const isVisible = (
        elRect.top >= containerRect.top &&
        elRect.bottom <= containerRect.bottom
      );
      
      if (!isVisible) {
        const offsetTop = elRect.top - containerRect.top;
        const currentScroll = container.scrollTop;
        const targetScroll = currentScroll + offsetTop - (container.clientHeight / 2) + (element.offsetHeight / 2);
        
        container.scrollTo({
          top: Math.max(0, Math.min(targetScroll, container.scrollHeight - container.clientHeight)),
          behavior: 'smooth'
        });
      }
    },

    updateViewport() {
      const mobile = window.innerWidth < 768;
      this.isMobile = mobile;
      document.body.classList.toggle('staffsync-mobile-nav', mobile);

      if (mobile) {
        this.$emit('nav-toggle', true);
        this.isCollapsed = !this.mobileMenuOpen;
      } else {
        this.mobileMenuOpen = false;
      }
    },

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      if (this.mobileMenuOpen) {
        this.isCollapsed = false;
        this.$emit('nav-toggle', true);
      }
    },

    closeMobileMenu() {
      if (!this.mobileMenuOpen) return;
      this.mobileMenuOpen = false;
      if (this.isMobile) {
        this.isCollapsed = true;
        this.$emit('nav-toggle', true);
      }
    },

    toggleNav() {
      if (this.isMobile) {
        this.toggleMobileMenu();
        return;
      }
      this.isCollapsed = !this.isCollapsed;
      this.$emit('nav-toggle', this.isCollapsed);
      // Collapse all submenus when nav collapses
      if (this.isCollapsed) {
        this.expandedItems = {};
      } else {
        // When expanding, scroll to active item
        this.$nextTick(() => this.scrollToActiveItem());
      }
    },
    
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
    },
    
    toggleSubmenu(item) {
      // Always toggle submenu expansion - using reactive update
      const newExpandedItems = { ...this.expandedItems };
      newExpandedItems[item.id] = !newExpandedItems[item.id];
      
      // Close other open dropdowns when opening a new one
      if (newExpandedItems[item.id]) {
        Object.keys(newExpandedItems).forEach(key => {
          if (key !== item.id) {
            newExpandedItems[key] = false;
          }
        });
      }
      
      this.expandedItems = newExpandedItems;
    },
    
    handleParentWithChildrenClick(item, event) {
      if (item.route) {
        this.goTo(item.route);
      }
      this.toggleSubmenu(item);
      
      // Scroll clicked item into view immediately
      if (event && event.currentTarget) {
        const container = this.$refs.navMenu;
        if (container) {
          this.scrollElementIntoView(container, event.currentTarget);
        }
      }
    },

    handleMenuItemClick(item, event) {
      if (item.external) {
        window.open(item.external, '_blank');
        return;
      }
      
      if (item.route) {
        this.saveNavScrollPosition();
        // Scroll the clicked item into view immediately
        if (event && event.currentTarget) {
          const container = this.$refs.navMenu;
          if (container) {
            this.scrollElementIntoView(container, event.currentTarget);
          }
        }
        
        this.goTo(item.route);
        this.showProfileMenu = false;
      }
    },
    
    isActive(route) {
      if (!route) return false;
      const r = String(route).replace(/^\/+/, '').toLowerCase();
      const name = (this.$route.name || '').toString().toLowerCase();
      const path = (this.$route.path || '').replace(/^#?\/+/, '').toLowerCase();
      return name === r || path === r || path.startsWith(r + '/');
    },
    
    isActiveParent(item) {
      if (!item.children) return false;
      return item.children.some(child => this.isActive(child.route));
    },
    
    goTo(route) {
      if (route) {
        this.saveNavScrollPosition();
        const path = route.startsWith('/') || route.startsWith('http') ? route : '/' + route;
        sessionStorage.setItem("selected", route);
        sessionStorage.setItem("selectedSub", route);
        sessionStorage.setItem("currentPage", route);
        this.$router.push(path);
        this.showProfileMenu = false;
        this.closeMobileMenu();
      }
    },
    
    isSuperAdmin() {
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || '{}');
        return String(userInfo.category || '').toLowerCase() === 'superadmin';
      } catch {
        return false;
      }
    },
    hasPermission(permission) {
      if (!permission) return true;
      
      // Support both single permission and array of permissions (OR logic)
      if (Array.isArray(permission)) {
        return permission.some(p => getRight(p));
      }
      
      return getRight(permission);
    },
    
    filterMenuItems() {
      const sectionsToUse = this.isSuperAdmin()
        ? this.allMenuSections.filter(s => s.title === 'Super Admin')
        : this.allMenuSections;

      if (!this.searchQuery.trim()) {
        this.filteredMenuSections = sectionsToUse.map(section => ({
          ...section,
          items: section.items.filter(item => this.filterItem(item))
        })).filter(section => section.items.length > 0);
        return;
      }
      
      const query = this.searchQuery.toLowerCase();
      this.filteredMenuSections = sectionsToUse.map(section => {
        const filteredItems = section.items.filter(item => {
          if (!this.filterItem(item)) return false;
          
          // Check if item matches search
          const matches = item.title.toLowerCase().includes(query);
          
          // Check if any child matches search
          const childMatches = item.children && item.children.some(child =>
            child.title.toLowerCase().includes(query) && this.childHasPermission(child)
          );
          
          // If searching, expand matching parents
          if (matches || childMatches) {
            if (item.children && item.children.length > 0) {
              const newExpandedItems = { ...this.expandedItems };
              newExpandedItems[item.id] = true;
              this.expandedItems = newExpandedItems;
            }
          }
          
          return matches || childMatches;
        });
        
        return { ...section, items: filteredItems };
      }).filter(section => section.items.length > 0);
      
      // After filtering, scroll to active item
      this.$nextTick(() => this.scrollToActiveItem());
    },
    
    visibleChildren(item) {
      if (!item?.children?.length) return [];
      return item.children.filter((child) => this.childHasPermission(child));
    },

    childHasPermission(child) {
      if (!child?.permission) return true;
      if (Array.isArray(child.permission)) {
        return child.permission.some((p) => this.hasPermission(p));
      }
      return this.hasPermission(child.permission);
    },

    filterItem(item) {
      if (item.children && item.children.length > 0) {
        const hasVisibleChildren = this.visibleChildren(item).length > 0;
        if (hasVisibleChildren) return true;
      }

      if (!item.permission) return true;
      if (Array.isArray(item.permission)) {
        return item.permission.some(p => this.hasPermission(p));
      }
      return this.hasPermission(item.permission);
    },

    onRolesUpdated() {
      this.getUserRole();
      this.filterMenuItems();
      this.$nextTick(() => this.scrollToActiveItem());
    },
    
    logout(route) {
      const data = new FormData();
      data.append("function", "logout");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      
      execute(data, authUrl)
        .then((res) => {
          sessionStorage.clear();
          this.$router.push(route);
        })
        .catch((e) => {
          console.error("Logout error:", e);
          sessionStorage.clear();
          this.$router.push(route);
        });
      
      this.showProfileMenu = false;
    },
    
    toggleTheme() {
      const currentTheme = document.body.getAttribute('data-theme') || 'light';
      const newTheme = currentTheme === 'light' ? 'dark' : 'light';
      document.body.setAttribute('data-theme', newTheme);
      localStorage.setItem('theme', newTheme);
    },
    
    showHelp() {
      this.$emit('show-help');
    },
    
    getUserRole() {
      const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || '{}');
      const selectedRoles = JSON.parse(sessionStorage.getItem("selectedRoles") || '[]');
      
      if (selectedRoles.length > 0) {
        const roleNames = selectedRoles.map(role => role.role_name || role.roleName).join(', ');
        this.userRole = roleNames;
      } else {
        const cat = (userInfo.category || '').trim();
        if (cat && /admin/i.test(cat)) {
          this.userRole = cat === 'admin' ? 'Admin' : cat;
        } else {
          this.userRole = userInfo.designation || 'Employee';
        }
      }
    },
    
    async ensureSessionRoles() {
      if (sessionStorage.getItem("isLoggedIn") !== "true") return;
      try {
        await refreshSessionRoles();
      } catch (e) {
        console.warn("Navigation: could not load roles — menu may be empty.", e);
      }
    },

    initializeMenu() {
      // Filter menu based on permissions
      this.filterMenuItems();
      
      // Get user info
      const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || '{}');
      this.sessionNames = userInfo.names || sessionStorage.getItem("sessionNames") || 'User';
      
      // Get user role
      this.getUserRole();
      
      // Expand current route's parent menu
      const currentRoute = this.currentRoute;
      const newExpandedItems = { ...this.expandedItems };
      
      this.allMenuSections.forEach(section => {
        section.items.forEach(item => {
          if (item.children) {
            const hasActiveChild = item.children.some(child => 
              this.hasPermission(child.permission) && (child.route === currentRoute || this.$route.path === child.route)
            );
            if (hasActiveChild) {
              newExpandedItems[item.id] = true;
            }
          }
        });
      });
      
      this.expandedItems = newExpandedItems;
    },
    
    handleClickOutside(event) {
      const profileClicked = event.target.closest('.nav-profile') || 
                            event.target.closest('.profile-dropdown');
      
      if (!profileClicked) {
        this.showProfileMenu = false;
      }
      
      // Close dropdowns when clicking outside
      const menuClicked = event.target.closest('.modern-nav');
      if (!menuClicked) {
        this.expandedItems = {};
      }
    },

    // Setup mutation observer for dynamic content
    setupMutationObserver() {
      const container = this.$refs.navMenu;
      if (container) {
        const observer = new MutationObserver((mutations) => {
          this.$nextTick(() => {
            this.scrollToActiveItem();
          });
        });
        
        observer.observe(container, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
        
        this.mutationObserver = observer;
      }
    }
  }
};
</script>

<style scoped>
/* ===== Modern Navigation Styles ===== */
.modern-nav {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 280px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.modern-nav.collapsed {
  width: 70px;
}

/* Brand Section */
.nav-brand {
  padding: 15px 12px;
  border-bottom: 1px solid #e2e8f0;
}

.brand-content {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
}

.brand-logo {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.logo-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.brand-text {
  flex: 1;
  overflow: hidden;
}

.brand-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.brand-subtitle {
  font-size: 11px;
  color: #64748b;
  margin: 2px 0 0 0;
}

.nav-toggle-btn {
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
  padding: 0;
}

.nav-toggle-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.toggle-icon {
  font-size: 12px;
  color: #64748b;
}

/* Profile Section */
.nav-profile {
  padding: 15px 12px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.profile-avatar {
  flex-shrink: 0;
}

.avatar-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-circle i {
  font-size: 18px;
  color: white;
}

.profile-info {
  flex: 1;
  overflow: hidden;
}

.profile-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-role {
  font-size: 11px;
  color: #64748b;
  margin: 2px 0 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-actions {
  flex-shrink: 0;
}

.profile-action-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.profile-action-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.profile-action-btn i {
  font-size: 14px;
  color: #64748b;
}

/* Profile Dropdown */
.profile-dropdown {
  position: absolute;
  top: 100%;
  left: 12px;
  right: 12px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e2e8f0;
  z-index: 1001;
  overflow: hidden;
  margin-top: 5px;
}

.dropdown-content {
  padding: 6px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  color: #475569;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
}

.dropdown-item:hover {
  background: #f1f5f9;
  color: #3b82f6;
}

.dropdown-item i {
  font-size: 16px;
  width: 18px;
  text-align: center;
}

.dropdown-item.logout {
  color: #ef4444;
}

.dropdown-item.logout:hover {
  background: #fee2e2;
}

.dropdown-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 6px 0;
}

/* Fade transition for dropdown */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Search Section */
.nav-search {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 34px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 12px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

/* Menu Section */
.nav-menu {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
  scroll-behavior: smooth;
}

.menu-section {
  margin-bottom: 16px;
}

.section-header {
  padding: 0 12px 6px 12px;
}

.section-title {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.menu-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Menu Item Wrapper - contains parent and submenu */
.menu-item-wrapper {
  position: relative;
}

/* Menu Item */
.menu-item,
.menu-item-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  border-radius: 8px;
  margin: 0 6px;
}

.menu-item:hover,
.menu-item-header:hover {
  background: #f1f5f9;
}

.menu-item.active,
.menu-item-header.active {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #ffffff;
}

.menu-item.collapsed,
.menu-item-header.collapsed {
  justify-content: center;
  padding: 8px;
}

.menu-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-icon i {
  font-size: 16px;
  color: #64748b;
  transition: all 0.2s;
}

.menu-item.active .menu-icon i,
.menu-item-header.active .menu-icon i {
  color: #ffffff;
}

.menu-item:hover .menu-icon i,
.menu-item-header:hover .menu-icon i {
  color: #3b82f6;
}

.menu-text {
  flex: 1;
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
}

.menu-title {
  font-size: 12px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-item.active .menu-title,
.menu-item-header.active .menu-title {
  color: #ffffff;
}

.menu-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 8px;
  background: #ef4444;
  color: #ffffff;
  margin-left: 6px;
}

.menu-arrow,
.menu-arrow-collapsed {
  flex-shrink: 0;
  margin-left: 6px;
}

.menu-arrow i,
.menu-arrow-collapsed i {
  font-size: 14px;
  color: #94a3b8;
  transition: all 0.2s;
}

.menu-arrow-collapsed {
  margin-left: auto;
}

.menu-item-header.active .menu-arrow i,
.menu-item-header.active .menu-arrow-collapsed i {
  color: #ffffff;
}

/* Tooltip for collapsed state */
.menu-tooltip {
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  background: #1e293b;
  color: #ffffff;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1000;
}

.menu-tooltip::before {
  content: '';
  position: absolute;
  left: -4px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-right: 4px solid #1e293b;
}

.menu-item.collapsed:hover .menu-tooltip,
.menu-item-header.collapsed:hover .menu-tooltip {
  opacity: 1;
}

/* Submenu (Dropdown) - positioned below parent */
.submenu {
  width: calc(100% - 12px);
  margin: 0 6px 4px 6px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  padding: 4px 0 4px 30px;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 7px 12px;
  cursor: pointer;
  transition: all 0.2s;
  margin: 2px 0;
  border-radius: 6px;
}

.submenu-item:hover {
  background: #e0e7ff;
}

.submenu-item.active {
  background: #e0e7ff;
}

.submenu-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submenu-icon i {
  font-size: 14px;
  color: #64748b;
}

.submenu-item.active .submenu-icon i {
  color: #4f46e5;
}

.submenu-text {
  flex: 1;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
}

.submenu-title {
  font-size: 11px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.submenu-item.active .submenu-title {
  color: #4f46e5;
  font-weight: 500;
}

.submenu-badge {
  font-size: 9px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 6px;
  background: #10b981;
  color: #ffffff;
  margin-left: 4px;
}

/* Slide down animation for dropdown */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 500px;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Navigation Footer */
.nav-footer {
  border-top: 1px solid #e2e8f0;
  padding: 12px;
}

.footer-content {
  display: flex;
  justify-content: space-around;
}

.footer-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  transition: all 0.2s;
  padding: 6px;
  border-radius: 6px;
}

.footer-item:hover {
  background: #f1f5f9;
}

.footer-item i {
  font-size: 16px;
  color: #64748b;
}

.footer-item:hover i {
  color: #3b82f6;
}

.footer-item span {
  font-size: 10px;
  color: #64748b;
}

/* Scrollbar Styling */
.nav-menu::-webkit-scrollbar {
  width: 4px;
}

.nav-menu::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.nav-menu::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.nav-menu::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.navigation-shell {
  position: relative;
}

.mobile-top-bar {
  display: none;
}

.nav-mobile-backdrop {
  display: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .mobile-top-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--staffsync-mobile-bar-height, 56px);
    padding: 0 12px;
    padding-top: env(safe-area-inset-top, 0px);
    background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
    color: #fff;
    z-index: 1100;
    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.15);
  }

  .mobile-menu-btn {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    cursor: pointer;
  }

  .mobile-top-bar__brand {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
  }

  .mobile-top-bar__title {
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.02em;
  }

  .mobile-top-bar__subtitle {
    font-size: 11px;
    opacity: 0.9;
  }

  .nav-mobile-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1045;
  }

  .modern-nav {
    transform: translateX(-100%);
    width: 280px;
    z-index: 1050;
    top: calc(var(--staffsync-mobile-bar-height, 56px) + env(safe-area-inset-top, 0px));
    height: calc(100vh - var(--staffsync-mobile-bar-height, 56px) - env(safe-area-inset-top, 0px));
  }
  
  .modern-nav.collapsed {
    transform: translateX(-100%);
  }
  
  .modern-nav.mobile-open {
    transform: translateX(0);
  }
  
  .nav-toggle-btn {
    display: none;
  }
}

/* Dark Theme Support */
[data-theme="dark"] .modern-nav {
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  border-right-color: #334155;
}

[data-theme="dark"] .nav-brand {
  border-bottom-color: #334155;
}

[data-theme="dark"] .brand-title {
  color: #f1f5f9;
}

[data-theme="dark"] .brand-subtitle {
  color: #94a3b8;
}

[data-theme="dark"] .nav-profile {
  border-bottom-color: #334155;
}

[data-theme="dark"] .profile-name {
  color: #f1f5f9;
}

[data-theme="dark"] .profile-role {
  color: #94a3b8;
}

[data-theme="dark"] .search-input {
  background: #334155;
  border-color: #475569;
  color: #f1f5f9;
}

[data-theme="dark"] .search-input:focus {
  background: #475569;
  border-color: #3b82f6;
}

[data-theme="dark"] .section-title {
  color: #94a3b8;
}

[data-theme="dark"] .menu-title {
  color: #e2e8f0;
}

[data-theme="dark"] .menu-item:hover,
[data-theme="dark"] .menu-item-header:hover {
  background: #334155;
}

[data-theme="dark"] .submenu {
  background: #1e293b;
  border-color: #334155;
}

[data-theme="dark"] .submenu-item:hover {
  background: #334155;
}

[data-theme="dark"] .submenu-item.active {
  background: rgba(79, 70, 229, 0.2);
}

[data-theme="dark"] .submenu-title {
  color: #cbd5e1;
}

[data-theme="dark"] .footer-item i {
  color: #cbd5e1;
}

[data-theme="dark"] .footer-item span {
  color: #94a3b8;
}

[data-theme="dark"] .nav-footer {
  border-top-color: #334155;
}

[data-theme="dark"] .profile-dropdown {
  background: #1e293b;
  border-color: #334155;
}

[data-theme="dark"] .dropdown-item {
  color: #cbd5e1;
}

[data-theme="dark"] .dropdown-item:hover {
  background: #334155;
  color: #3b82f6;
}

[data-theme="dark"] .dropdown-divider {
  background: #334155;
}
</style>